/**
 * BottomActionSheet Component
 *
 * Mobile-first action sheet that slides up from the bottom of the viewport.
 * Used for quick actions like export, share, and navigation on mobile devices.
 *
 * Features:
 * - Slide-up animation with backdrop overlay
 * - Tap-to-dismiss on backdrop
 * - Swipe-down gesture dismiss (50px threshold)
 * - Close button (×) in header
 * - Escape key dismiss
 * - Respects prefers-reduced-motion
 * - Focus trap for accessibility
 *
 * @see Story 5.3: Bottom Action Sheet
 */

import { useDrag } from '@use-gesture/react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface ActionItem {
  /** Display label for the action */
  label: string;
  /** Icon element (React node) */
  icon: React.ReactNode;
  /** Click handler */
  onClick: () => void;
  /** Optional: Mark as destructive (red styling) */
  destructive?: boolean;
}

export interface BottomActionSheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback when sheet should close */
  onClose: () => void;
  /** Optional title for the sheet header */
  title?: string;
  /** Array of action items to display */
  actions: ActionItem[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SWIPE_THRESHOLD = 50; // Minimum pixels to swipe down to dismiss

// ============================================================================
// COMPONENT
// ============================================================================

export function BottomActionSheet({
  isOpen,
  onClose,
  title = 'Acoes',
  actions,
}: BottomActionSheetProps): JSX.Element | null {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap - focus first action when opening
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      const firstButton = sheetRef.current.querySelector('button');
      if (firstButton) {
        firstButton.focus();
      }
    }
  }, [isOpen]);

  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Swipe-down gesture handler
  const bind = useDrag(
    ({ movement: [, my], direction: [, dy], cancel, active }) => {
      // Only allow downward swipes
      if (my > 0 && dy > 0) {
        setDragY(my);
      }

      // Dismiss when threshold exceeded and gesture is released
      if (!active && my > SWIPE_THRESHOLD && dy > 0) {
        onClose();
        setDragY(0);
        if (cancel) cancel();
      }

      // Reset drag when gesture ends without dismissing
      if (!active) {
        setDragY(0);
      }
    },
    {
      axis: 'y',
      filterTaps: true,
    }
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Calculate transform based on drag
  const sheetTransform = dragY > 0 ? `translateY(${dragY}px)` : 'translateY(0)';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 ${
          prefersReducedMotion ? '' : 'transition-opacity duration-300'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-brand-dark ${
          prefersReducedMotion ? '' : 'transition-transform duration-300 ease-out'
        }`}
        style={{
          transform: sheetTransform,
          maxHeight: '80vh',
          touchAction: 'none',
        }}
        {...bind()}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-brand-gray/50" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-brand-dark-lighter">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 -mr-2 rounded-full text-brand-gray hover:text-white hover:bg-brand-dark-lighter transition-colors"
            aria-label="Fechar"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Actions */}
        <div className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 100px)' }}>
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick();
                onClose();
              }}
              className={`flex items-center w-full gap-3 px-4 py-3 min-h-[48px] rounded-xl text-left transition-colors ${
                action.destructive
                  ? 'text-red-400 hover:bg-red-500/10 active:bg-red-500/20'
                  : 'text-white hover:bg-brand-dark-lighter active:bg-brand-dark-light'
              }`}
            >
              <span className="flex-shrink-0 w-6 h-6">{action.icon}</span>
              <span className="font-medium">{action.label}</span>
            </button>
          ))}
        </div>

        {/* Safe area padding for iOS */}
        <div className="h-safe-area-inset-bottom pb-safe" />
      </div>
    </>
  );
}

export default BottomActionSheet;
